import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import CountryCodeSelect from '../components/CountryCodeSelect';
import { crmAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import {
  QrCode,
  Smartphone,
  Share2,
  TrendingUp,
  CheckCircle2,
  ArrowRight,
  Star,
  Users,
  Zap,
  Shield,
  Globe,
  Clock,
  Phone,
  Mail,
  MessageSquare,
  User,
  Send,
  Check,
  X,
  Play,
  Sparkles,
  CreditCard,
  Building,
  ChevronRight
} from 'lucide-react';

const FreeBusinessCard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  // Demo booking modal state
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [demoForm, setDemoForm] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+91',
    message: ''
  });
  const [isSubmittingDemo, setIsSubmittingDemo] = useState(false);
  const [demoSuccess, setDemoSuccess] = useState(false);

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    if (!demoForm.name.trim()) {
      toast({ title: "Name required", description: "Please enter your name", variant: "destructive" });
      return;
    }
    if (!demoForm.email.trim() && !demoForm.phone.trim()) {
      toast({ title: "Contact required", description: "Please enter your email or phone number", variant: "destructive" });
      return;
    }
    setIsSubmittingDemo(true);
    try {
      await crmAPI.submitDemoRequest('mraja', {
        name: demoForm.name.trim(),
        email: demoForm.email.trim() || null,
        phone: demoForm.phone.trim() ? `${demoForm.countryCode}${demoForm.phone.trim()}` : null,
        message: demoForm.message.trim() || null,
        source: "Free Business Card Landing Page"
      });
      setDemoSuccess(true);
      toast({ title: "Request Sent!", description: "We'll contact you soon." });
      setTimeout(() => {
        setDemoForm({ name: '', email: '', phone: '', countryCode: '+91', message: '' });
        setDemoSuccess(false);
        setShowDemoModal(false);
      }, 2000);
    } catch (error) {
      toast({ title: "Error", description: "Failed to submit. Please try again.", variant: "destructive" });
    } finally {
      setIsSubmittingDemo(false);
    }
  };

  const features = [
    {
      icon: QrCode,
      title: 'Instant QR Code Generation',
      description: 'Get your personalized QR code in seconds. Anyone can scan it to save your contact instantly.'
    },
    {
      icon: Smartphone,
      title: 'Works on All Devices',
      description: 'No app needed. Your digital card opens perfectly on any smartphone, tablet, or computer.'
    },
    {
      icon: TrendingUp,
      title: 'Track Your Scans',
      description: 'Know exactly when and where people scan your QR code. Follow up with warm leads.'
    },
    {
      icon: Zap,
      title: 'Update Anytime',
      description: 'Changed your number? New job? Update your card once, everyone sees the new info instantly.'
    },
    {
      icon: Shield,
      title: 'Privacy Protected',
      description: 'You control what information is shared. Your data is secure and never sold.'
    },
    {
      icon: Globe,
      title: 'Share Anywhere',
      description: 'Email signature, social media, print materials. Your QR code works everywhere.'
    }
  ];

  const steps = [
    {
      number: '01',
      title: 'Enter Your Details',
      description: 'Add your name, title, phone, email, and social links in under 2 minutes.'
    },
    {
      number: '02',
      title: 'Choose Your Theme',
      description: 'Pick from 7 professional themes designed for different industries.'
    },
    {
      number: '03',
      title: 'Get Your QR Code',
      description: 'Download your personalized QR code and start sharing immediately.'
    }
  ];

  const testimonials = [
    {
      name: 'Priya Sharma',
      role: 'Real Estate Agent, Mumbai',
      content: 'My lead capture increased by 47% after switching to Piper Card. The QR code on my yard signs works 24/7!',
      rating: 5
    },
    {
      name: 'Rahul Verma',
      role: 'Sales Manager, Bangalore',
      content: 'No more lost business cards at conferences. Everyone just scans my QR code and I get their contact too.',
      rating: 5
    },
    {
      name: 'Dr. Anjali Patel',
      role: 'Dentist, Delhi',
      content: 'Patients love how easy it is to share my contact with friends. Referrals have never been easier.',
      rating: 5
    }
  ];

  const faqs = [
    {
      question: 'Is it really 100% free?',
      answer: 'Yes! Our digital business card with QR code is completely free forever. No hidden charges, no credit card required. Premium features like NFC physical cards are optional upgrades.'
    },
    {
      question: 'Do people need an app to scan my QR code?',
      answer: 'No. Any smartphone camera can scan QR codes natively since 2017. The person scanning does not need any app installed.'
    },
    {
      question: 'Can I update my information later?',
      answer: 'Absolutely! That\'s the main advantage. Update your details anytime, and everyone with your QR code automatically sees the new information.'
    },
    {
      question: 'How is this different from a paper business card?',
      answer: 'Paper cards get lost, information goes stale, and you can\'t track who has your contact. Digital QR cards are always updated, trackable, and impossible to lose.'
    },
    {
      question: 'Can I use this for my team?',
      answer: 'Yes! Create cards for your entire team. Each person gets their own QR code linked to their profile. Team management features available.'
    }
  ];

  // Structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Free Digital Business Cards with QR Code | Piper Card",
    "description": "Create free digital business cards with QR codes. Share contact info instantly. No design skills needed. Best free business card maker in India.",
    "url": "https://pipercard.com/free-business-card",
    "mainEntity": {
      "@type": "Product",
      "name": "Piper Card - Free Digital Business Card",
      "description": "Create professional digital business cards with QR codes for free",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "INR",
        "availability": "https://schema.org/InStock"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "reviewCount": "2847"
      }
    }
  };

  return (
    <Layout>
      <SEO
        title="Free Digital Business Cards with QR Code | Create in 2 Minutes | Piper Card"
        description="Create stunning free digital business cards with QR codes. Share your contact info instantly with a scan. No design skills needed. Best free business card maker in India 2026."
        keywords="free business cards, free digital business card, QR code business card, free business card maker, digital visiting card free, QR code generator free, business card with QR code free, create business card online free, free NFC business card India"
        canonical="https://pipercard.com/free-business-card"
        ogType="website"
        ogImage="https://pipercard.com/og-free-business-card.png"
        twitterCard="summary_large_image"
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-amber-50 via-white to-orange-50 pt-20 pb-16 lg:pt-28 lg:pb-24">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                100% Free Forever
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                Create Free
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-orange-600"> Digital Business Cards </span>
                with QR Code
              </h1>
              
              <p className="text-lg sm:text-xl text-gray-600 mb-8 max-w-xl mx-auto lg:mx-0">
                Share your contact info instantly with a simple scan. No design skills needed. 
                Create your professional digital card in under 2 minutes.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
                <Button
                  size="lg"
                  onClick={() => navigate('/card-builder')}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-8 py-6 text-lg rounded-xl shadow-lg shadow-amber-500/25"
                  data-testid="hero-create-free-btn"
                >
                  Create Free Card Now
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => setShowDemoModal(true)}
                  className="border-2 border-gray-300 px-8 py-6 text-lg rounded-xl"
                  data-testid="hero-demo-btn"
                >
                  <Play className="mr-2 w-5 h-5" />
                  Watch Demo
                </Button>
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-6 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  <span>No Credit Card Required</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  <span>Setup in 2 Minutes</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  <span>Free Forever Plan</span>
                </div>
              </div>
            </div>

            {/* Right Content - QR Code Demo */}
            <div className="relative">
              <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md mx-auto">
                <div className="text-center mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-amber-100 to-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <QrCode className="w-10 h-10 text-amber-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Your QR Code</h3>
                  <p className="text-gray-500 text-sm">Scan to see how it works</p>
                </div>
                
                <div className="bg-gray-50 rounded-2xl p-6 mb-6">
                  <img 
                    src="/piper-logo-qr.png" 
                    alt="Sample QR Code Business Card" 
                    className="w-48 h-48 mx-auto"
                  />
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-sm text-gray-600">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-green-600" />
                    </div>
                    <span>Instant contact sharing</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-600">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-green-600" />
                    </div>
                    <span>Works on all smartphones</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-600">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-green-600" />
                    </div>
                    <span>Update anytime, free forever</span>
                  </div>
                </div>
              </div>

              {/* Floating Stats */}
              <div className="absolute -left-4 top-1/4 bg-white rounded-xl shadow-lg p-4 hidden lg:block">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                    <Users className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">50,000+</p>
                    <p className="text-xs text-gray-500">Cards Created</p>
                  </div>
                </div>
              </div>

              <div className="absolute -right-4 bottom-1/4 bg-white rounded-xl shadow-lg p-4 hidden lg:block">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <Star className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">4.9/5</p>
                    <p className="text-xs text-gray-500">User Rating</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Everything You Need, Completely Free
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Create professional digital business cards with powerful features. No hidden costs, no premium gates.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-amber-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Create Your Free Card in 3 Simple Steps
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              No design skills needed. No complicated setup. Just enter your details and you're ready to network like a pro.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-2xl p-8 shadow-lg relative z-10">
                  <div className="text-6xl font-bold text-amber-100 mb-4">{step.number}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-20">
                    <ChevronRight className="w-8 h-8 text-amber-400" />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button
              size="lg"
              onClick={() => navigate('/card-builder')}
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-8 py-6 text-lg rounded-xl"
              data-testid="steps-create-btn"
            >
              Start Creating - It's Free
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Loved by 50,000+ Professionals
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              See why professionals across India are switching to digital business cards with QR codes.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 italic">"{testimonial.content}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center text-white font-bold">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{testimonial.name}</p>
                      <p className="text-sm text-gray-500">{testimonial.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Everything you need to know about free digital business cards with QR codes.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <Card key={index} className="border-0 shadow-md">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{faq.question}</h3>
                  <p className="text-gray-600">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-r from-amber-500 to-orange-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Create Your Free Digital Business Card?
          </h2>
          <p className="text-xl text-amber-100 mb-8 max-w-2xl mx-auto">
            Join 50,000+ professionals who are networking smarter with QR code business cards. 
            Setup takes less than 2 minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={() => navigate('/card-builder')}
              className="bg-white text-amber-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-xl shadow-lg"
              data-testid="final-cta-btn"
            >
              Create Your Free Card
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/blog/free-business-cards-with-qr-code')}
              className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg rounded-xl"
            >
              Read Full Guide
            </Button>
          </div>
        </div>
      </section>

      {/* Demo Modal */}
      <Dialog open={showDemoModal} onOpenChange={setShowDemoModal}>
        <DialogContent className="sm:max-w-md">
          <div className="p-6">
            {demoSuccess ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Request Sent!</h3>
                <p className="text-gray-600">We'll contact you shortly.</p>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900">Get a Personal Demo</h3>
                  <p className="text-gray-600 text-sm">See how Piper Card can help you network better</p>
                </div>
                <form onSubmit={handleDemoSubmit} className="space-y-4">
                  <div>
                    <Label>Name *</Label>
                    <Input
                      value={demoForm.name}
                      onChange={(e) => setDemoForm({ ...demoForm, name: e.target.value })}
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={demoForm.email}
                      onChange={(e) => setDemoForm({ ...demoForm, email: e.target.value })}
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <div className="flex gap-2">
                      <CountryCodeSelect
                        value={demoForm.countryCode}
                        onChange={(code) => setDemoForm({ ...demoForm, countryCode: code })}
                        className="w-24"
                      />
                      <Input
                        value={demoForm.phone}
                        onChange={(e) => setDemoForm({ ...demoForm, phone: e.target.value })}
                        placeholder="9876543210"
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    disabled={isSubmittingDemo}
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-500"
                  >
                    {isSubmittingDemo ? 'Sending...' : 'Request Demo'}
                  </Button>
                </form>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default FreeBusinessCard;
